import React from 'react';
import { Link } from 'react-router-dom';
import { styled, keyframes } from '@mui/system';
import Typography from '@mui/material/Typography';
import { ArrowRight, Clock, Check } from 'lucide-react';
import { getLocalizedPath } from 'helpers/getLocale';
import { L } from 'pages/Simulator/strings';
import Troll from 'assets/images/careerTest/troll-greetings.png';
import professionCases from 'helpers/professionCases';

// Banner after the AI-risk section on occupations that have a simulator. The
// "image" is a small illustration of the simulator itself (chat, KPIs, a
// picked answer), so it previews what the player will actually do.

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
`;

const dots = keyframes`
  0%, 60%, 100% { opacity: 0.35; transform: translateY(0); }
  30% { opacity: 1; transform: translateY(-3px); }
`;

const REDUCED = '@media (prefers-reduced-motion: reduce)';

const Wrapper = styled(Link)(({ theme }) => ({
  position: 'relative',
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 520px)',
  alignItems: 'center',
  minHeight: 250,
  marginBottom: 25,
  borderRadius: 15,
  overflow: 'hidden',
  textDecoration: 'none',
  color: 'inherit',
  backgroundColor: '#eef8dd',
  backgroundImage: 'radial-gradient(rgba(121, 201, 18, 0.22) 1px, transparent 1px)',
  backgroundSize: '18px 18px',
  '&:hover .sim-lift': { transform: 'translateY(-5px)' },
  '&:hover .sim-cta svg': { transform: 'translateX(4px)' },
  '&:focus-visible': { outline: `3px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
    minHeight: 0
  },
  [theme.breakpoints.down('sm')]: { marginBottom: 20 }
}));

const Scene = styled('div')(({ theme }) => ({
  position: 'relative',
  height: '100%',
  minHeight: 250,
  [theme.breakpoints.down('md')]: { minHeight: 255 }
}));

const MiniCard = styled('div')(({ theme, top, left, delay, width }) => ({
  position: 'absolute',
  top,
  left,
  width,
  padding: '14px 16px',
  borderRadius: 16,
  backgroundColor: theme.palette.white.main,
  boxShadow: '0 10px 24px rgba(34, 34, 34, 0.08), 0 1px 2px rgba(34, 34, 34, 0.06)',
  transition: 'transform 250ms ease',
  animation: `${float} 4s ease-in-out ${delay}s infinite`,
  [REDUCED]: { animation: 'none' },
  [theme.breakpoints.down('sm')]: {
    maxWidth: '80%',
    boxSizing: 'border-box',
    '&.wide-only': { display: 'none' }
  }
}));

// Hover lift lives on an inner wrapper so it doesn't fight the float animation.
const Lift = styled('div')(() => ({ transition: 'transform 250ms ease' }));

const Chip = styled('span')(({ theme }) => ({
  position: 'absolute',
  top: 22,
  left: '44%',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 12px',
  borderRadius: 100,
  backgroundColor: theme.palette.black.main,
  color: theme.palette.primary.salad,
  fontFamily: 'Rubik-Regular',
  fontSize: 13,
  fontWeight: 500,
  [theme.breakpoints.down('sm')]: { display: 'none' }
}));

const Row = styled('div')(() => ({ display: 'flex', alignItems: 'center', gap: 8 }));

const Dot = styled('span')(({ color, size = 32 }) => ({
  width: size,
  height: size,
  minWidth: size,
  borderRadius: '50%',
  backgroundColor: color
}));

const Skel = styled('span')(({ w, color = '#ebedee', h = 8 }) => ({
  display: 'block',
  width: w,
  height: h,
  borderRadius: 100,
  backgroundColor: color
}));

const Typing = styled('span')(() => ({
  display: 'inline-flex',
  gap: 3,
  padding: '6px 9px',
  borderRadius: 12,
  backgroundColor: '#f2f3ef',
  '& i': {
    width: 5,
    height: 5,
    borderRadius: '50%',
    backgroundColor: '#9aa093',
    animation: `${dots} 1.1s ease-in-out infinite`
  },
  '& i:nth-of-type(2)': { animationDelay: '0.15s' },
  '& i:nth-of-type(3)': { animationDelay: '0.3s' },
  [REDUCED]: { '& i': { animation: 'none' } }
}));

const Bar = styled('span')(({ value, color }) => ({
  display: 'block',
  height: 8,
  borderRadius: 100,
  backgroundColor: '#ebedee',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    display: 'block',
    width: `${value}%`,
    height: '100%',
    borderRadius: 100,
    backgroundColor: color
  }
}));

const Picked = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '7px 9px',
  borderRadius: 10,
  border: `2px solid ${theme.palette.primary.main}`,
  backgroundColor: 'rgba(121, 201, 18, 0.06)'
}));

const Plus = styled('span')(() => ({
  marginLeft: 'auto',
  padding: '1px 7px',
  borderRadius: 100,
  fontFamily: 'Rubik-Regular',
  fontSize: 11,
  fontWeight: 500,
  color: '#4f9a04',
  backgroundColor: 'rgba(121, 201, 18, 0.16)'
}));

const Mascot = styled('img')(({ theme }) => ({
  position: 'absolute',
  right: 4,
  bottom: -6,
  width: 130,
  height: 130,
  objectFit: 'contain',
  [theme.breakpoints.down('lg')]: { display: 'none' },
  [theme.breakpoints.down('md')]: { display: 'block', width: 96, height: 96, right: 12 },
  [theme.breakpoints.down('sm')]: { display: 'none' }
}));

const Label = styled('div')(({ theme }) => ({
  margin: '24px 30px 24px 0',
  padding: '22px 26px',
  borderRadius: 15,
  backgroundColor: theme.palette.secondary.main,
  color: theme.palette.black.main,
  textAlign: 'center',
  [theme.breakpoints.down('md')]: { margin: '0 16px 16px', padding: '18px 16px' }
}));

const Title = styled(Typography)(({ theme }) => ({
  fontSize: 21,
  lineHeight: '28px',
  fontWeight: 600,
  marginBottom: 8,
  [theme.breakpoints.down('md')]: { fontSize: 18, lineHeight: '24px' }
}));

const Text = styled(Typography)(() => ({
  fontSize: 16,
  lineHeight: '22px',
  marginBottom: 16,
  opacity: 0.85
}));

const Cta = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 10,
  padding: '10px 20px',
  borderRadius: 8,
  backgroundColor: theme.palette.black.main,
  color: theme.palette.white.main,
  fontFamily: 'Rubik-Regular',
  fontSize: 16,
  fontWeight: 500,
  '& svg': { transition: 'transform 160ms ease' }
}));

const Meta = styled('span')(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  marginLeft: 12,
  fontFamily: 'Rubik-Regular',
  fontSize: 14,
  verticalAlign: 'middle'
}));

const SimulatorBanner = ({ simulator, professionInfo, t }) => (
  <Wrapper to={getLocalizedPath(`/simulator/${simulator.id}`)}>
    <Scene aria-hidden="true">
      <Chip>
        <Clock size={14} /> 09:05
      </Chip>
      <MiniCard top={30} left="5%" width={270} delay={0}>
        <Lift className="sim-lift">
          <Row style={{ marginBottom: 10 }}>
            <Dot color="#30b0c7" />
            <div style={{ flex: 1 }}>
              <Skel w="50%" color="#cfd3d6" />
              <Skel w="90%" style={{ marginTop: 6 }} />
              <Skel w="70%" style={{ marginTop: 6 }} />
            </div>
          </Row>
          <Row>
            <Dot color="#e25510" />
            <Typing>
              <i />
              <i />
              <i />
            </Typing>
          </Row>
        </Lift>
      </MiniCard>
      <MiniCard className="wide-only" top={70} left="56%" width={190} delay={0.8}>
        <Lift className="sim-lift">
          <Skel w="55%" color="#cfd3d6" style={{ marginBottom: 12 }} />
          <Bar value={72} color="#79c912" />
          <Bar value={41} color="#d5921b" style={{ marginTop: 9 }} />
          <Bar value={63} color="#79c912" style={{ marginTop: 9 }} />
        </Lift>
      </MiniCard>
      <MiniCard top={150} left="16%" width={290} delay={1.6}>
        <Lift className="sim-lift">
          <Picked>
            <Check size={16} color="#4f9a04" />
            <Skel w="55%" color="#cfd3d6" />
            <Plus>+5</Plus>
          </Picked>
          <Row style={{ padding: '10px 10px 0' }}>
            <Skel w="75%" />
          </Row>
        </Lift>
      </MiniCard>
      <Mascot src={Troll} alt="" width={130} height={130} loading="lazy" />
    </Scene>
    <Label>
      <Title component="p">
        {t('SimulatorTitle', professionCases(professionInfo, 'genitive'))}
      </Title>
      <Text>{L(simulator.tagline)}</Text>
      <div>
        <Cta className="sim-cta">
          {t('SimulatorCta')} <ArrowRight size={18} />
        </Cta>
        <Meta>
          <Clock size={15} /> {t('SimulatorMinutes', { n: simulator.minutes })}
        </Meta>
      </div>
    </Label>
  </Wrapper>
);

export default SimulatorBanner;
